import { Check, CalendarPlus, Clock, MapPin } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useBooking } from '../hooks/useBooking';
import { format, addMinutes } from 'date-fns';
import { formatTimeForDisplay } from '../utils/availability';
import { formatPrice } from '../types';

/**
 * Build an .ics calendar file content string and trigger a download.
 */
function downloadIcsFile({
  title,
  startDate,
  startTime,
  durationMinutes,
  description,
  location,
}: {
  title: string;
  startDate: Date;
  startTime: string; // "HH:MM"
  durationMinutes: number;
  description: string;
  location: string;
}) {
  const dateStr = format(startDate, 'yyyyMMdd');
  const [hours, minutes] = startTime.split(':').map(Number);
  const startDt = new Date(startDate);
  startDt.setHours(hours, minutes, 0, 0);
  const endDt = addMinutes(startDt, durationMinutes);

  const formatDt = (d: Date) => format(d, "yyyyMMdd'T'HHmmss");

  const escapeIcs = (text: string) =>
    text.replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\n/g, '\\n');

  const ics = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//BookingSite//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `DTSTART:${formatDt(startDt)}`,
    `DTEND:${formatDt(endDt)}`,
    `SUMMARY:${escapeIcs(title)}`,
    `DESCRIPTION:${escapeIcs(description)}`,
    `LOCATION:${escapeIcs(location)}`,
    `STATUS:CONFIRMED`,
    'END:VEVENT',
    'END:VCALENDAR',
  ].join('\r\n');

  const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'appointment.ics';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function SuccessPage() {
  const { bookingState, businessListing, resetBooking } = useBooking();
  const { selectedService, selectedDate, selectedTime, customerInfo } = bookingState;
  const location = useLocation();

  // Determine booking status from router state, falling back to confirmation_mode
  const bookingStatus: 'confirmed' | 'pending' =
    (location.state as any)?.bookingStatus ||
    (businessListing?.confirmation_mode === 'instant' ? 'confirmed' : 'pending');
  const isConfirmed = bookingStatus === 'confirmed';


  const handleAddToCalendar = () => {
    if (!selectedService || !selectedDate || !selectedTime) return;

    const descriptionParts: string[] = [];
    if (customerInfo.notes) {
      descriptionParts.push(`client requests: ${customerInfo.notes}`);
    }
    descriptionParts.push(`Service: ${selectedService.name}`);
    descriptionParts.push(`Duration: ${selectedService.duration_minutes} minutes`);
    descriptionParts.push(`Price: ${formatPrice(selectedService.price_cents, selectedService.currency)}`);

    if (businessListing?.public_phone) {
      descriptionParts.push(`Phone: ${businessListing.public_phone}`);
    }
    if (businessListing?.public_email) {
      descriptionParts.push(`Email: ${businessListing.public_email}`);
    }
    downloadIcsFile({
      title: `${selectedService.name} – ${businessListing?.business_name || 'Appointment'}`,
      startDate: selectedDate,
      startTime: selectedTime,
      durationMinutes: selectedService.duration_minutes,
      description: descriptionParts.join('\n'),
      location: businessListing?.business_name || '',
    });
  };

  return (
    <div className="min-h-screen bg-site-bg text-site-text flex items-center justify-center p-4">
      <div className="max-w-lg w-full">
        {/* Success Card */}
        <div className="bg-site-bg rounded-3xl border border-site-border p-8 text-center">
          {/* Success Icon */}
          <div
            className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5"
            style={{ backgroundColor: 'var(--site-primary)', border: '1px solid var(--site-primary-outline)' }}
          >
            <Check className="w-8 h-8 text-site-on-primary" strokeWidth={3} />
          </div>

          {/* Main Message */}
          <h1 className="text-2xl font-bold text-site-text mb-1">
            {isConfirmed ? 'Booking Confirmed!' : 'Booking Request Received!'}
          </h1>

          <p className="text-site-muted mb-6">
            {isConfirmed
              ? 'Your booking is confirmed!'
              : (
                <>
                  Your booking request is pending approval.
                  {businessListing?.public_phone && (
                    <> Call{' '}
                      <a
                        href={`tel:${businessListing.public_phone}`}
                        className="font-medium underline"
                        style={{ color: 'var(--site-link)' }}
                      >
                        {businessListing.public_phone}
                      </a>{' '}for questions.
                    </>
                  )}
                </>
              )
            }
          </p>

          {/* Compact Booking Summary */}
          {selectedService && selectedDate && selectedTime && (
            <div className="bg-site-bg rounded-2xl p-5 mb-6 text-left">
              <div className="flex items-start gap-4">
                {/* Date block */}
                <div
                  className="flex-shrink-0 w-14 h-14 rounded-xl flex flex-col items-center justify-center text-site-on-primary"
                  style={{ backgroundColor: 'var(--site-primary)', border: '1px solid var(--site-primary-outline)' }}
                >
                  <span className="text-xs font-semibold uppercase leading-none">
                    {format(selectedDate, 'MMM')}
                  </span>
                  <span className="text-xl font-bold leading-none mt-0.5">
                    {format(selectedDate, 'd')}
                  </span>
                </div>

                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-site-text truncate">
                    {selectedService.name}
                  </h3>
                  <p className="text-sm text-site-muted mt-0.5">
                    {format(selectedDate, 'EEEE')} at {formatTimeForDisplay(selectedTime)}
                  </p>
                  <div className="flex items-center gap-3 mt-1.5 text-xs text-site-muted">
                    <span className="flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5" />
                      {selectedService.duration_minutes} min
                    </span>
                    <span className="font-medium text-site-text">
                      {formatPrice(selectedService.price_cents, selectedService.currency)}
                    </span>
                  </div>
                </div>
              </div>

              {businessListing?.business_name && (
                <div className="flex items-center gap-1.5 mt-3 pt-3 border-t border-site-border text-sm text-site-muted">
                  <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                  <span>{businessListing.business_name}</span>
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="space-y-3">
            {selectedService && selectedDate && selectedTime && (
              <button
                onClick={handleAddToCalendar}
                className="w-full font-semibold py-3 rounded-xl transition-colors flex items-center justify-center gap-2 text-site-on-primary"
                style={{ backgroundColor: 'var(--site-primary)', border: '1px solid var(--site-primary-outline)' }}
              >
                <CalendarPlus className="w-5 h-5" />
                Add to Calendar
              </button>
            )}

            <button
              onClick={() => {
                resetBooking();
                window.location.href = '/book';
              }}
              className="w-full bg-site-text/5 text-site-text font-semibold py-3 rounded-xl hover:bg-site-text/10 transition-colors"
            >
              Book Another Appointment
            </button>
          </div>
        </div>

        {/* Contact Info */}
        {(businessListing?.public_email || businessListing?.public_phone) && (
          <div className="mt-5 text-center">
            <p className="text-sm text-site-muted mb-1">
              Need to make changes?
            </p>
            <div className="text-sm">
              {businessListing.public_email && (
                <a
                  href={`mailto:${businessListing.public_email}`}
                  className="font-medium hover:underline"
                  style={{ color: 'var(--site-link)' }}
                >
                  {businessListing.public_email}
                </a>
              )}
              {businessListing.public_phone && businessListing.public_email && (
                <span className="mx-2 text-site-muted">•</span>
              )}
              {businessListing.public_phone && (
                <a
                  href={`tel:${businessListing.public_phone}`}
                  className="font-medium hover:underline"
                  style={{ color: 'var(--site-link)' }}
                >
                  {businessListing.public_phone}
                </a>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
